import apiClient from './authService';

const messageVariableValuesService = {
  /**
   * Get all active variables for a specific entity (FlowNode or CampaignMessage)
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @returns {Promise<Array>} Array of variables
   */
  async getVariablesByEntityId(entityType, entityId) {
    try {
      const response = await apiClient.get(`/message-variable-values/entity/${entityType}/${entityId}`);
      // Handle response structure - ResponseUtil wraps in { success, message, data, timestamp }
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.warn(`Could not fetch variables for ${entityType} ${entityId}:`, error.message);
      // Don't throw - allow fallback to single variable from flow config
      return [];
    }
  },

  /**
   * Get all variables (including soft-deleted) for a specific entity
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @returns {Promise<Array>} Array of all variables
   */
  async getAllVariablesByEntityId(entityType, entityId) {
    try {
      const response = await apiClient.get(`/message-variable-values/entity/${entityType}/${entityId}/all`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching all variables for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Get a specific variable by ID
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @param {string} msgVarId - Message Variable ID
   * @returns {Promise<Object>} Variable object
   */
  async getVariableById(entityType, entityId, msgVarId) {
    try {
      const response = await apiClient.get(`/message-variable-values/${msgVarId}`, {
        params: { entityType, entityId }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching variable ${msgVarId} for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Add a single variable
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @param {Object} variableData - Variable data
   * @param {string} variableData.variableExpression - Variable expression (e.g., "{{1}}")
   * @param {string} variableData.value - Variable value
   * @param {string} variableData.type - Variable type (TEXT or PARAMS)
   * @returns {Promise<Object>} Created variable object
   */
  async addVariable(entityType, entityId, variableData) {
    try {
      const requestData = {
        entityType,
        entityId,
        variableExpression: variableData.variableExpression,
        value: variableData.value || '',
        type: variableData.type || 'TEXT'
      };
      const response = await apiClient.post('/message-variable-values', requestData);
      return response.data;
    } catch (error) {
      console.error(`Error adding variable for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Add multiple variables in bulk
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @param {Array} variablesData - Array of variable data objects
   * @returns {Promise<Array>} Array of created variable objects
   */
  async addVariables(entityType, entityId, variablesData) {
    try {
      const requestData = variablesData.map(variable => ({
        entityType,
        entityId,
        variableExpression: variable.variableExpression,
        value: variable.value || '',
        type: variable.type || 'TEXT'
      }));
      const response = await apiClient.post('/message-variable-values/bulk', requestData);
      return response.data;
    } catch (error) {
      console.error(`Error adding variables for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Update a variable
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @param {string} msgVarId - Message Variable ID
   * @param {Object} updateData - Update data
   * @param {string} [updateData.value] - New variable value
   * @param {string} [updateData.type] - New variable type
   * @returns {Promise<Object>} Updated variable object
   */
  async updateVariable(entityType, entityId, msgVarId, updateData) {
    try {
      const response = await apiClient.put(`/message-variable-values/${msgVarId}`, {
        ...updateData,
        entityType,
        entityId
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating variable ${msgVarId} for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Soft delete a variable
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @param {string} msgVarId - Message Variable ID
   * @returns {Promise<Object>} Success response
   */
  async deleteVariable(entityType, entityId, msgVarId) {
    try {
      const response = await apiClient.delete(`/message-variable-values/${msgVarId}`, {
        params: { entityType, entityId }
      });
      return response.data;
    } catch (error) {
      console.error(`Error deleting variable ${msgVarId} for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Soft delete all variables for an entity
   * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @returns {Promise<Object>} Success response
   */
  async deleteAllVariables(entityType, entityId) {
    try {
      const response = await apiClient.delete(`/message-variable-values/entity/${entityType}/${entityId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting all variables for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Count active variables for an entity
  * @param {string} entityType - Entity type (BOT_NODE or CAMPAIGN_MESSAGE)
   * @param {string} entityId - Entity ID (nodeId or messageId)
   * @returns {Promise<number>} Count of active variables
   */
  async countActiveVariablesByEntityId(entityType, entityId) {
    try {
      const response = await apiClient.get(`/message-variable-values/entity/${entityType}/${entityId}/count`);
      return response.data;
    } catch (error) {
      console.error(`Error counting active variables for ${entityType} ${entityId}:`, error);
      throw error;
    }
  }
};

export { messageVariableValuesService };

